import { useCallback, useEffect, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { REALADVISOR_TENANT_ID } from '@realadvisor/constants';
import { Copy, Trash2 } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import type { Actions } from '@modules/_shared/components/actions-menu/ActionsMenu';
import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import { LeadExitContext } from '@modules/_shared/components/lead/leadExit';
import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { getContactDisplayNames } from '@modules/_shared/utils/contact';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { formatAddress } from '@modules/_shared/utils/formatting';
import { DesktopLeadDetailsLayout } from '@modules/lead-details/components/DesktopLeadDetailsLayout';
import { MobileLeadDetailsLayout } from '@modules/lead-details/components/MobileLeadDetailsLayout';
import { useLeadPermissions } from '@modules/lead-details/hooks/useLeadPermissions';
import { GET_LEADS, GET_LEAD_AGENTS } from '@modules/leads/leadsQueries';
import { useLocale } from '@shared/hooks/locale';
import { fromGlobalId } from '@shared/utils/global-id';

export const GET_LEAD_DETAILS = gql(/* GraphQL */ `
  query GetLeadDetails($lead_id: uuid!) {
    leads_by_pk(id: $lead_id) {
      id
      origin_enquiry_id
      ...LeadUpdatedTimeAgo
      created_by
      anonymized_contact
      appraisal_perceived_value
      appraisal_reason
      relationship
      appraisal_open_to_offer
      appraisal_property_occupied
      ...LeadAppraisalLocation
      property_usage
      potential_development
      sale_horizon
      referrer
      referring_domain
      landing_page_url
      source {
        id
        label
        name
      }
      phone_number
      email_address
      origin_enquiry {
        ...UserInfo_enquiry
      }
      contact {
        id
        first_name
        last_name
        display_name
        phone_numbers(order_by: [{ primary: desc }, { verified: desc }]) {
          id
          number
        }
        emails(
          order_by: [
            { primary: desc }
            { verified: desc }
            { created_at: desc }
          ]
        ) {
          id
          email
        }
        property_searches(order_by: { updated_at: desc }) {
          ...PropertySearchesCardPropertySearches
        }
      }
      lots {
        id
        updated_at
        offer_type
        sale_price
        rent_net
        rent_extra
        currency
        price_unit
        hide_price_on_portals
        use_dummy_address
        dummy_route
        dummy_street_number
        dummy_postcode
        dummy_locality
        property {
          id
          route
          street_number
          postcode
          locality
          country
          country_code
          computed_surface
          latest_appraisal {
            id
            value
            price_per_living_surface_meter_squared
          }
          property_images(
            limit: 1
            order_by: { order_nr: asc }
            where: { display_on_listing: { _eq: true } }
          ) {
            image {
              url
            }
          }
        }
      }
      property {
        id
        lat
        lng
        google_address {
          lat
          lng
          formatted_address
        }
        property_files {
          property_id
          file_id
          file {
            id
            name
            url
            mimetype
            created_at
            uploader {
              first_name
              last_name
            }
          }
        }
        street_number
        route
        postcode
        locality
        state
        country_code
        property_type {
          label
          name
          main_type
        }
        # Surfaces
        plot_number
        living_surface
        built_surface
        usable_surface
        gross_floor_surface
        balcony_surface
        terrace_surface
        garden_surface
        land_surface
        basement_surface
        weighted_floor_surface
        building_volume
        residential_surface
        commercial_surface
        # Rooms
        number_of_rooms
        number_of_bedrooms
        number_of_bathrooms
        number_of_toilets
        # Units
        number_of_residential_units
        number_of_commercial_units
        # Floors
        number_of_floors
        floor_of_flat
        number_of_lifts
        side
        is_oriented_north
        is_oriented_south
        is_oriented_east
        is_oriented_west
        # Parking
        number_of_indoor_parkings
        number_of_outdoor_parkings
        # HVAC
        heating
        heating_distribution
        cooling
        # Energy efficiency
        energy_consumption_class
        gas_emissions_class
        consumption
        emissions
        energy_reference_year
        energy_inspection_date
        estimated_lower_energy_cost
        estimated_higher_energy_cost
        energy_certificate_version
        # Construction
        construction_year
        renovation_year
        building_standard
        building_condition
        minergie_code
        # Location
        micro_location
        macro_location
        # Rental income
        is_residential_fully_rented
        residential_yearly_rental_income
        residential_potential_rental_income
        is_commercial_fully_rented
        commercial_yearly_rental_income
        commercial_potential_rental_income
        income_includes_parking
        is_parking_fully_rented
        parking_yearly_rental_income
        parking_potential_rental_income
        charges
        renovation_fund
        # Equipment
        has_gym
        has_view
        has_sauna
        has_cellar
        has_fireplace
        has_storage_room
        has_swimming_pool
        has_wellness_area
        has_elevator
        has_photovoltaic_panels
        has_attic
        # Features
        is_luxury
        is_child_friendly
        is_wheelchair_accessible
        is_new_development
        appraisals(limit: 1, order_by: { created_at: desc }) {
          id
          min
          max
          value
          updated_at
        }
      }
      lead_agents(where: { passed_at: { _is_null: true } }) {
        id
        user_id
        claimed_at
        passed_at
        win_probability
        predicted_win_date
        lead_stage {
          id
          label
          status
        }
        user {
          id
          first_name
          last_name
          display_name
          organisation {
            name
          }
        }
      }
      leads_tags {
        id
        dictionary {
          id
          label
        }
      }
      cma_reports(order_by: { updated_at: desc }) {
        id
        created_by
        suggested_market_value
        is_price_range
        price_range_min
        price_range_max
        updated_at
        created_at
        report_source
      }
    }
  }
`);

const DELETE_LEAD = gql(/* GraphQL */ `
  mutation LeadDetailsDrawerDeleteLead($lead_id: uuid!) {
    delete_leads_by_pk(id: $lead_id) {
      id
    }
  }
`);

const GET_LEAD_AGENT_STATUS = gql(/* GraphQL */ `
  query GetLeadAgentStatusDrawer($lead_id: uuid!) {
    lead_agents(
      where: { lead_id: { _eq: $lead_id }, passed_at: { _is_null: true } }
    ) {
      id
      lead_stage {
        id
        label
        status
        pipeline {
          id
        }
      }
      user {
        id
        first_name
        last_name
      }
    }
  }
`);

const GET_DUPLICATE_STAGE = gql(/* GraphQL */ `
  query GetDuplicateStageDrawer {
    lead_stages(
      where: {
        pipeline: { name: { _eq: "brokerage" } }
        name: { _eq: "is_a_duplicate" }
      }
      limit: 1
    ) {
      id
    }
  }
`);

const SELF_ASSIGN_LEAD_AGENT = gql(/* GraphQL */ `
  mutation SelfAssignLeadAgentDrawer(
    $lead_id: uuid!
    $broker_id: uuid!
    $stage_id: uuid!
    $claimed_at: timestamptz!
  ) {
    insert_lead_agents_one(
      object: {
        lead_id: $lead_id
        user_id: $broker_id
        source: crm
        stage_id: $stage_id
        claimed_at: $claimed_at
      }
    ) {
      id
      ...LeadAgent
    }
  }
`);

type LeadDetailsDrawerProps = {
  leadId?: string;
  onClose?: () => void;
};

export const LeadDetailsDrawer = ({
  leadId: leadIdProp,
  onClose,
}: LeadDetailsDrawerProps = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLocale();
  let { leadId: leadIdParam } = useParams() as { leadId: string };

  // temporary fix to support legacy lead ids
  try {
    const decodedLegacyLeadId = fromGlobalId(leadIdParam);
    if (decodedLegacyLeadId) {
      leadIdParam = decodedLegacyLeadId;
    }
  } catch {
    // do nothing
  }

  const leadId = leadIdProp ?? leadIdParam;

  const [title, setTitle] = useState<string>(t('Lead'));
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down('md'));
  const { me } = useAppData();
  const isAdmin = me?.is_admin === true;

  const { data, loading, refetch } = useQuery(GET_LEAD_DETAILS, {
    variables: { lead_id: leadId ?? '' },
    skip: !leadId,
  });

  const lead = data?.leads_by_pk;
  const permissions = useLeadPermissions({ lead });

  const [deleteLead, { loading: isDeleting }] = useMutation(DELETE_LEAD, {
    onCompleted: () => {
      emitFeedRefresh();
      // Same exit as closing: a drawer opened over another page (onClose)
      // closes in place instead of going up a route.
      if (onClose) {
        onClose();
        return;
      }
      navigate({
        pathname: '..',
        search: location.search,
      });
    },
    refetchQueries: [GET_LEADS],
    awaitRefetchQueries: true,
    onError: setErrorLike,
  });

  // Duplicate lead logic - only available for RealAdvisor admins
  const shouldFetchDuplicateStage =
    isAdmin && me?.tenant_id === REALADVISOR_TENANT_ID;

  const { data: leadAgentData } = useQuery(GET_LEAD_AGENT_STATUS, {
    variables: { lead_id: leadId ?? '' },
    skip: !leadId,
  });

  const leadAgents = leadAgentData?.lead_agents ?? [];

  const { data: duplicateStageData, loading: duplicateStageLoading } = useQuery(
    GET_DUPLICATE_STAGE,
    {
      skip: !shouldFetchDuplicateStage,
    },
  );

  const duplicateStageId = duplicateStageData?.lead_stages[0]?.id ?? null;

  // Self-assign lead as duplicate (assigns current user and sets stage)
  const [selfAssignLeadAgent, { loading: assignDuplicateLoading }] =
    useMutation(SELF_ASSIGN_LEAD_AGENT, {
      onError: setErrorLike,
      refetchQueries: [GET_LEADS, GET_LEAD_AGENT_STATUS, GET_LEAD_AGENTS],
      onCompleted: () => {
        emitFeedRefresh();
      },
      awaitRefetchQueries: true,
    });

  // Only show duplicate button if no agents assigned yet
  const canShowDuplicateButton =
    shouldFetchDuplicateStage && leadAgents.length === 0;

  const handleMarkAsDuplicate = useCallback(async () => {
    if (!leadId || !me?.id || !duplicateStageId) {
      return;
    }

    await selfAssignLeadAgent({
      variables: {
        lead_id: leadId,
        broker_id: me.id,
        stage_id: duplicateStageId,
        claimed_at: new Date().toISOString(),
      },
    });
  }, [me?.id, duplicateStageId, leadId, selfAssignLeadAgent]);

  // Update drawer title with contact name and address
  useEffect(() => {
    if (!lead) {
      return;
    }

    const { displayName: name } = getContactDisplayNames(
      lead,
      permissions.canView,
    );
    const address = permissions.canView
      ? lead.property?.google_address?.formatted_address ??
        formatAddress(lead.property ?? {}, ', ', true)
      : lead.property?.locality ?? '';

    const prefix = [name, address]
      .filter((part): part is string => Boolean(part && part.trim() !== ''))
      .join(' - ');

    setTitle(prefix ? `${prefix} / ${t('Leads')}` : t('Lead'));
  }, [lead, permissions.canView, t]);

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    } else {
      navigate({
        pathname: '..',
        search: location.search,
      });
    }
  }, [onClose, navigate, location.search]);

  const handleAgentAssignmentChange = useCallback(
    (keepOpen: boolean) => {
      if (keepOpen) {
        refetch();
      } else {
        handleClose();
      }
    },
    [refetch, handleClose],
  );

  return (
    <LeadExitContext.Provider value={handleClose}>
      <Drawer
        onClose={handleClose}
        title={title}
        actionsLoading={isDeleting}
        actions={
          isAdmin || canShowDuplicateButton
            ? ([
                canShowDuplicateButton
                  ? {
                      label: t('Mark as duplicate'),
                      disabled:
                        duplicateStageLoading ||
                        assignDuplicateLoading ||
                        duplicateStageId == null,
                      onClick: handleMarkAsDuplicate,
                      icon: <Copy size={16} />,
                    }
                  : null,
                isAdmin
                  ? {
                      label: t('Delete'),
                      disabled: false,
                      onClick: () => {
                        if (
                          window.confirm(
                            t('Are you sure you want to delete this lead?'),
                          )
                        ) {
                          deleteLead({ variables: { lead_id: leadId ?? '' } });
                        }
                      },
                      icon: <Trash2 size={16} />,
                    }
                  : null,
              ].filter(Boolean) as Actions[])
            : undefined
        }
      >
        {loading && <LoadingSpinner />}

        {!loading && lead && (
          <Box
            sx={{
              width: '100%',
              maxWidth: 1400,
              minWidth: 0,
              mx: 'auto',
            }}
          >
            {isMobileLayout ? (
              <MobileLeadDetailsLayout
                lead={lead}
                leadId={leadId ?? ''}
                permissions={permissions}
                refetch={refetch}
                onAssignmentChange={handleAgentAssignmentChange}
              />
            ) : (
              <DesktopLeadDetailsLayout
                lead={lead}
                leadId={leadId ?? ''}
                permissions={permissions}
                refetch={refetch}
                onAssignmentChange={handleAgentAssignmentChange}
              />
            )}
          </Box>
        )}
      </Drawer>

      <MutationErrorSnackbar
        onClose={() => setErrorLike(null)}
        error={apolloError}
      />
    </LeadExitContext.Provider>
  );
};
